import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  Box,
  Card,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Avatar,
  IconButton,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import StorageIcon from '@mui/icons-material/Storage'
import SaveIcon from '@mui/icons-material/Save'
import AddIcon from '@mui/icons-material/Add'
import { productService, inventoryService, divisionService } from '../services'
import CachedImage from '../components/CachedImage'
import ProductInventoryLotsPanel from '../components/product-management/ProductInventoryLotsPanel'
import {
  formatVatPercentDisplay,
  formatVatPercentForForm,
  formatDsdDisplay,
  getProductManageReturnPath,
  parseVatPercentInput,
  resolveProductDetailReturnPath,
  getEditProductFieldErrors,
  isPackSizeApiErrorMessage,
  PACK_SIZE_FIELD_ERROR,
  resolvePackSizeForPayload,
} from '../utils/productManagementUtils'
import { getBarcodeUpdateErrorMessage } from '../utils/barcodeErrors'
import { normalizeMovementPageResponse } from '../utils/inventoryMovementPagination'
import { useAllPaginatedItems } from '../hooks/useAllPaginatedItems'

const ProductDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const [product, setProduct] = useState(null)
  const [inventory, setInventory] = useState(null)
  const [movements, setMovements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [movementsPage, setMovementsPage] = useState(0)
  const [movementsRowsPerPage, setMovementsRowsPerPage] = useState(10)
  const [movementsTotal, setMovementsTotal] = useState(0)
  const movementsRequestId = useRef(0)
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false)
  const [inventoryFormData, setInventoryFormData] = useState({})
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editFormData, setEditFormData] = useState({})
  const [editFieldErrors, setEditFieldErrors] = useState({})
  const [barcodes, setBarcodes] = useState([])
  const [barcodeDraft, setBarcodeDraft] = useState([])
  const [barcodeInput, setBarcodeInput] = useState('')
  const [barcodeEditMode, setBarcodeEditMode] = useState(false)
  const [barcodeError, setBarcodeError] = useState(null)
  const fetchAllDivisions = useCallback((params) => divisionService.getDivisions(params), [])
  const { items: divisions } = useAllPaginatedItems(fetchAllDivisions)
  const [divisionEditMode, setDivisionEditMode] = useState(false)
  const [divisionDraftId, setDivisionDraftId] = useState('')
  const [vatEditMode, setVatEditMode] = useState(false)
  const [vatDraft, setVatDraft] = useState('')
  const [dsdEditMode, setDsdEditMode] = useState(false)
  const [dsdDraft, setDsdDraft] = useState(false)
  const [inventoryRefreshKey, setInventoryRefreshKey] = useState(0)

  const fetchProductDetails = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await productService.getProductById(id)
      // Non-paginated response: getProductById returns a single product object
      setProduct(response || {})
      const barcodeResponse = await productService.getProductBarcodes(id)
      const barcodeValues = (barcodeResponse?.items || [])
        .map((item) => item?.barcode)
        .filter((v) => typeof v === 'string' && v.trim().length > 0)
      setBarcodes(barcodeValues)
      setBarcodeDraft(barcodeValues)
      setBarcodeEditMode(false)
      setBarcodeInput('')
      setBarcodeError(null)

      // If we have article code (sku), fetch inventory
      if (response?.sku) {
        const sku = response.sku
        try {
          const inventoryResponse = await inventoryService.getInventoryBySku(sku)
          // Non-paginated response: getInventoryBySku returns a single inventory object
          setInventory(inventoryResponse || {})
        } catch (invError) {
          console.log('No inventory found for this product')
        }
      }
    } catch (err) {
      console.error('Error fetching product details:', err)
      setError(err.response?.data?.message || 'Failed to fetch product details')
    } finally {
      setLoading(false)
    }
  }, [id])

  const fetchMovements = useCallback(async () => {
    const requestId = ++movementsRequestId.current
    if (!product?.sku) {
      return
    }

    try {
      const response = await inventoryService.getInventoryMovements(product.sku, {
        page: movementsPage + 1,
        limit: movementsRowsPerPage
      })

      const movementPage = normalizeMovementPageResponse(response)
      if (requestId !== movementsRequestId.current) {
        return
      }
      setMovements(movementPage.items)
      setMovementsTotal(movementPage.total)
    } catch (err) {
      if (requestId !== movementsRequestId.current) {
        return
      }
      console.log('No movements found for this product')
      setMovements([])
      setMovementsTotal(0)
    }
  }, [product?.sku, movementsPage, movementsRowsPerPage])

  useEffect(() => {
    fetchProductDetails()
  }, [fetchProductDetails])

  // Fetch movements when product changes or pagination changes
  useEffect(() => {
    if (product?.sku) {
      fetchMovements()
    }
  }, [product?.sku, movementsPage, movementsRowsPerPage, fetchMovements])

  const handleBack = () => {
    const returnPath = resolveProductDetailReturnPath(
      searchParams,
      location.state,
    )
    if (returnPath) {
      navigate(returnPath)
      return
    }
    navigate(getProductManageReturnPath(new URLSearchParams()))
  }

  const handleEditProduct = () => {
    setEditFormData({
      product_name: product.product_name,
      description: product.description || '',
      sku: product.sku,
      category: product.category_name || '',
      division_id: product.division_id || '',
      brand: product.brand || '',
      tp_map: product.tp_map || '',
      tax_category: product.tax_category || '',
      unit_of_measure: product.unit_of_measure || '',
      vat: formatVatPercentForForm(product.vat),
      dsd: product.dsd === true,
      pack_size: product.pack_size ?? '',
      pack_type: product.pack_type || '',
      image_logo_url: product.image_url?.logo || '',
      image_portrait_url: product.image_url?.portrait || '',
      image_banner_url: product.image_url?.banner || '',
      additional_images: product.image_url?.additional_images ? JSON.stringify(product.image_url.additional_images) : '',
      is_active: product.is_active !== false,
      is_purchasable: product.is_purchasable !== false,
      source: product.source || 'shwapno',
    })
    setEditFieldErrors({})
    setActionError(null)
    setEditDialogOpen(true)
  }

  const handleEditDialogClose = () => {
    setEditDialogOpen(false)
    setEditFormData({})
    setActionError(null)
    setEditFieldErrors({})
  }

  const handleEditFormChange = (field, value) => {
    setEditFormData({
      ...editFormData,
      [field]: value,
    })
    if (field === 'pack_size' && editFieldErrors.pack_size) {
      setEditFieldErrors((prev) => {
        const next = { ...prev }
        delete next.pack_size
        return next
      })
    }
  }

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true)
      setActionError(null)
      setEditFieldErrors({})

      const fieldErrors = getEditProductFieldErrors(editFormData)
      if (Object.keys(fieldErrors).length > 0) {
        setEditFieldErrors(fieldErrors)
        return
      }

      // Format image data for backend
      const imageData = {}
      if (editFormData.image_logo_url) imageData.logo = editFormData.image_logo_url
      if (editFormData.image_portrait_url) imageData.portrait = editFormData.image_portrait_url
      if (editFormData.image_banner_url) imageData.banner = editFormData.image_banner_url
      if (editFormData.additional_images) {
        try {
          // Try to parse as JSON array first
          if (editFormData.additional_images.trim().startsWith('[')) {
            imageData.additional_images = JSON.parse(editFormData.additional_images);
          } else {
            // Fallback to comma-separated parsing
            imageData.additional_images = editFormData.additional_images
              .split(',')
              .map(url => url.trim())
              .filter(url => url.length > 0);
          }
        } catch (error) {
          // If JSON parsing fails, try comma-separated fallback
          imageData.additional_images = editFormData.additional_images
            .split(',')
            .map(url => url.trim())
            .filter(url => url.length > 0);
        }
      }

      const vatParsed = parseVatPercentInput(editFormData.vat)
      if (vatParsed.kind === 'invalid') {
        setActionError(vatParsed.reason)
        return
      }

      const updateData = {
        product_name: editFormData.product_name,
        description: editFormData.description,
        sku: editFormData.sku,
        category: editFormData.category,
        division_id: editFormData.division_id || null,
        brand: editFormData.brand || null,
        tp_map: editFormData.tp_map?.trim() ? editFormData.tp_map.trim() : null,
        tax_category: editFormData.tax_category || null,
        unit_of_measure: editFormData.unit_of_measure || null,
        pack_size: resolvePackSizeForPayload(editFormData.pack_size),
        pack_type: editFormData.pack_type?.trim()
          ? editFormData.pack_type.trim().toLowerCase()
          : null,
        image_url: Object.keys(imageData).length > 0 ? imageData : null,
        is_active: editFormData.is_active,
      }
      if (vatParsed.kind === 'value') {
        updateData.vat = vatParsed.value
      }
      if (editFormData.source !== 'external' && product?.source !== 'external') {
        updateData.is_purchasable = editFormData.is_purchasable !== false
        updateData.dsd = editFormData.dsd === true
      }

      // Send update request to backend
      await productService.updateProduct(product.id, updateData)

      setSuccessMessage('Product updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)

      handleEditDialogClose()
      // Refresh product data
      fetchProductDetails()
    } catch (err) {
      console.error('Error updating product:', err)
      const message = err.response?.data?.message || 'Failed to update product'
      if (isPackSizeApiErrorMessage(message)) {
        setEditFieldErrors({ pack_size: PACK_SIZE_FIELD_ERROR })
        return
      }
      setActionError(message)
    } finally {
      setActionLoading(false)
    }
  }

  const addBarcodeToDraft = () => {
    const next = String(barcodeInput || '').trim()
    if (!next) return
    if (['none', 'null'].includes(next.toLowerCase())) return
    if (barcodeDraft.some((b) => String(b).trim() === next)) {
      setBarcodeError(`Barcode "${next}" already exists in this product.`)
      return
    }
    setBarcodeDraft((prev) => [...prev, next])
    setBarcodeInput('')
    setBarcodeError(null)
  }

  const removeBarcodeFromDraft = (barcodeValue) => {
    setBarcodeDraft((prev) => prev.filter((b) => b !== barcodeValue))
  }

  const handleStartDivisionEdit = () => {
    setDivisionDraftId(product.division_id || '')
    setDivisionEditMode(true)
  }

  const handleCancelDivisionEdit = () => {
    setDivisionEditMode(false)
    setDivisionDraftId('')
  }

  const handleSaveDivisionEdit = async () => {
    try {
      setActionLoading(true)
      setActionError(null)
      await productService.updateProduct(product.id, {
        division_id: divisionDraftId || null,
      })
      setSuccessMessage('Division updated successfully')
      setDivisionEditMode(false)
      fetchProductDetails()
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update division')
    } finally {
      setActionLoading(false)
    }
  }

  const handleStartVatEdit = () => {
    setVatDraft(formatVatPercentForForm(product.vat))
    setVatEditMode(true)
  }

  const handleCancelVatEdit = () => {
    setVatEditMode(false)
    setVatDraft('')
  }

  const handleSaveVatEdit = async () => {
    const vatParsed = parseVatPercentInput(vatDraft)
    if (vatParsed.kind === 'invalid') {
      setActionError(vatParsed.reason)
      return
    }
    if (vatParsed.kind === 'absent') {
      setActionError('VAT% is required')
      return
    }

    try {
      setActionLoading(true)
      setActionError(null)
      await productService.updateProduct(product.id, {
        vat: vatParsed.value,
      })
      setSuccessMessage('VAT% updated successfully')
      setVatEditMode(false)
      fetchProductDetails()
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update VAT%')
    } finally {
      setActionLoading(false)
    }
  }

  const handleStartDsdEdit = () => {
    setDsdDraft(product.dsd === true)
    setDsdEditMode(true)
  }

  const handleCancelDsdEdit = () => {
    setDsdEditMode(false)
    setDsdDraft(false)
  }

  const handleSaveDsdEdit = async () => {
    if (product.source === 'external' && dsdDraft === true) {
      setActionError('DSD can only be enabled for shwapno products')
      return
    }
    try {
      setActionLoading(true)
      setActionError(null)
      await productService.updateProduct(product.id, {
        dsd: dsdDraft === true,
      })
      setSuccessMessage('DSD updated successfully')
      setDsdEditMode(false)
      fetchProductDetails()
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update DSD')
    } finally {
      setActionLoading(false)
    }
  }

  const handleStartBarcodeEdit = () => {
    setBarcodeDraft(barcodes)
    setBarcodeInput('')
    setBarcodeEditMode(true)
    setBarcodeError(null)
  }

  const handleCancelBarcodeEdit = () => {
    setBarcodeDraft(barcodes)
    setBarcodeInput('')
    setBarcodeEditMode(false)
    setBarcodeError(null)
  }

  const handleSaveBarcodeEdit = async () => {
    try {
      setActionLoading(true)
      setBarcodeError(null)
      const normalized = barcodeDraft
        .map((v) => String(v || '').trim())
        .filter((v) => v && !['none', 'null'].includes(v.toLowerCase()))
      const deduped = Array.from(new Set(normalized))
      const response = await productService.replaceProductBarcodes(product.id, deduped)
      const fresh = (response?.items || []).map((item) => item.barcode).filter(Boolean)
      setBarcodes(fresh)
      setBarcodeDraft(fresh)
      setBarcodeEditMode(false)
      setBarcodeInput('')
      setSuccessMessage('Barcodes updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      console.error('Error updating barcodes:', err)
      setBarcodeError(getBarcodeUpdateErrorMessage(err))
    } finally {
      setActionLoading(false)
    }
  }

  const handleEditInventory = () => {
    // Open inventory dialog instead of navigating
    setInventoryFormData({
      quantity_on_hand: parseInt(inventory?.quantity_on_hand, 10) || 0,
      minimum_stock_level: parseInt(inventory?.minimum_stock_level, 10) || 0,
      maximum_stock_level: parseInt(inventory?.maximum_stock_level, 10) || 0,
      reorder_point: parseInt(inventory?.reorder_point, 10) || 0,
      reserved_quantity: parseInt(inventory?.reserved_quantity, 10) || 0,
      location: inventory?.location || '',
      notes: inventory?.notes || '',
    })
    setInventoryDialogOpen(true)
  }

  const handleInventoryDialogClose = () => {
    setInventoryDialogOpen(false)
    setInventoryFormData({})
    setActionError(null)
  }

  const handleInventoryFormChange = (field, value) => {
    let parsedValue;

    // Integer fields
    if (['minimum_stock_level', 'maximum_stock_level', 'reorder_point', 'quantity_on_hand'].includes(field)) {
      parsedValue = parseInt(value, 10) || 0;
    }
    // Integer fields for reserved_quantity
    else if (['reserved_quantity'].includes(field)) {
      parsedValue = parseInt(value, 10) || 0;
    }
    // String fields
    else {
      parsedValue = value;
    }

    setInventoryFormData({
      ...inventoryFormData,
      [field]: parsedValue,
    })
  }

  const handleSaveInventory = async () => {
    try {
      setActionLoading(true)
      setActionError(null)

      if (!product?.sku) {
        setActionError('Product Article Code is required for inventory update')
        return
      }

      await inventoryService.createOrUpdateInventory(product.sku, {
        ...inventoryFormData,
        reason: 'Manual update from product details',
        metadata: {
          source: 'product_details_page',
          product_id: product.id,
          product_name: product.product_name,
          updated_at: new Date().toISOString()
        }
      })

      setSuccessMessage('Inventory updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)

      // Refresh inventory data
      if (product.sku) {
        try {
          const inventoryResponse = await inventoryService.getInventoryBySku(product.sku)
          setInventory(inventoryResponse || {})
          setInventoryRefreshKey((prev) => prev + 1)

          // Also refresh movements to show the latest data
          await fetchMovements()
        } catch (invError) {
          console.log('No inventory found for this product')
        }
      }

      handleInventoryDialogClose()

    } catch (err) {
      console.error('Error updating inventory:', err)
      setActionError(err.response?.data?.message || 'Failed to update inventory')
    } finally {
      setActionLoading(false)
    }
  }

  const handleMovementsPageChange = (event, newPage) => {
    setMovementsPage(newPage)
  }

  const handleMovementsRowsPerPageChange = (event) => {
    setMovementsRowsPerPage(parseInt(event.target.value, 10))
    setMovementsPage(0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getMovementTypeColor = (type) => {
    const colors = {
      create: { bg: '#e8f5e9', color: '#2e7d32' },
      update: { bg: '#e3f2fd', color: '#1565c0' },
      stock_in: { bg: '#e8f5e9', color: '#2e7d32' },
      stock_out: { bg: '#fff3e0', color: '#e65100' },
      adjustment: { bg: '#f3e5f5', color: '#7b1fa2' },
      correction: { bg: '#ffebee', color: '#c62828' }
    }
    return colors[type] || { bg: '#f5f5f5', color: '#757575' }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Products
        </Button>
      </Box>
    )
  }

  if (!product) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Product not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Products
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Products
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
          {product.product_name}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
          Article Code: {product.sku} • Category: {product.category_name || 'Uncategorized'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Product Information */}
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Product Information
              </Typography>
              {/* <Button
                startIcon={<EditIcon />}
                onClick={handleEditProduct}
                variant="outlined"
                size="small"
              >
                Edit Product
              </Button> */}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Product Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{product.product_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Article Code</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{product.sku}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Category</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {product.category_image_url && (
                    <Avatar
                      src={product.category_image_url}
                      alt={product.category_name}
                      sx={{ width: 24, height: 24 }}
                    />
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {product.category_name || 'Uncategorized'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Division</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {divisionEditMode ? (
                    <>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <Select
                          value={divisionDraftId}
                          onChange={(e) => setDivisionDraftId(e.target.value)}
                        >
                          {divisions.map((division) => (
                            <MenuItem key={division.id} value={division.id}>
                              {division.name} ({division.slug})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button size="small" onClick={handleCancelDivisionEdit} disabled={actionLoading}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveDivisionEdit}
                        disabled={actionLoading}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Chip
                        label={product.division_name || product.division_slug || 'Default'}
                        size="small"
                        variant="outlined"
                      />
                      <IconButton size="small" onClick={handleStartDivisionEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Grid>
              {product.brand && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Brand</Typography>
                  <Typography variant="body1">{product.brand}</Typography>
                </Grid>
              )}
              {product.tp_map && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">TP/MAP</Typography>
                  <Typography variant="body1">{product.tp_map}</Typography>
                </Grid>
              )}
              {product.unit_of_measure && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Unit of Measure</Typography>
                  <Typography variant="body1">{product.unit_of_measure}</Typography>
                </Grid>
              )}
              {product.pack_size != null && product.pack_size > 0 && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Pack Size</Typography>
                  <Typography variant="body1">{product.pack_size}</Typography>
                </Grid>
              )}
              {product.pack_type && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Pack Type</Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{product.pack_type}</Typography>
                </Grid>
              )}
              {product.tax_category && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Tax Category</Typography>
                  <Typography variant="body1">{product.tax_category}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">VAT %</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {vatEditMode ? (
                    <>
                      <TextField
                        size="small"
                        type="number"
                        value={vatDraft}
                        onChange={(e) => setVatDraft(e.target.value)}
                        disabled={actionLoading}
                        inputProps={{ step: '0.01', min: 0, max: 100 }}
                        sx={{ width: 120 }}
                      />
                      <Button size="small" onClick={handleCancelVatEdit} disabled={actionLoading}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveVatEdit}
                        disabled={actionLoading}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="body1">{formatVatPercentDisplay(product.vat)}</Typography>
                      <IconButton size="small" onClick={handleStartVatEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">DSD</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {dsdEditMode ? (
                    <>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={dsdDraft === true}
                            onChange={(e) => setDsdDraft(e.target.checked)}
                            disabled={actionLoading || product.source === 'external'}
                          />
                        }
                        label={dsdDraft ? 'Yes' : 'No'}
                      />
                      <Button size="small" onClick={handleCancelDsdEdit} disabled={actionLoading}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveDsdEdit}
                        disabled={actionLoading || (product.source === 'external' && dsdDraft === true)}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="body1">{formatDsdDisplay(product.dsd)}</Typography>
                      {product.source !== 'external' && (
                        <IconButton size="small" onClick={handleStartDsdEdit}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={product.status}
                  size="small"
                  sx={{
                    backgroundColor: product.status === 'published' ? '#e8f5e9' : '#fff3e0',
                    color: product.status === 'published' ? '#2e7d32' : '#e65100',
                    fontWeight: 500
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Available for purchase</Typography>
                <Chip
                  label={product.is_purchasable !== false ? 'Yes' : 'No'}
                  size="small"
                  sx={{
                    backgroundColor: product.is_purchasable !== false ? '#e8f5e9' : '#ffebee',
                    color: product.is_purchasable !== false ? '#2e7d32' : '#c62828',
                    fontWeight: 500,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Created Date</Typography>
                <Typography variant="body1">{formatDate(product.created_at)}</Typography>
              </Grid>
              {product.description && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{product.description}</Typography>
                </Grid>
              )}
            </Grid>
          </Card>

          <Card sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Barcodes
              </Typography>
              {!barcodeEditMode ? (
                <Button
                  startIcon={<EditIcon />}
                  onClick={handleStartBarcodeEdit}
                  variant="outlined"
                  size="small"
                >
                  Edit
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={handleCancelBarcodeEdit}
                    color="inherit"
                    size="small"
                    disabled={actionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveBarcodeEdit}
                    variant="contained"
                    size="small"
                    startIcon={actionLoading ? <CircularProgress size={14} /> : <SaveIcon />}
                    disabled={actionLoading}
                    sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#d32f2f' } }}
                  >
                    Save
                  </Button>
                </Box>
              )}
            </Box>

            {barcodeError && (
              <Alert
                severity="error"
                onClose={() => setBarcodeError(null)}
                sx={{ mb: 2 }}
              >
                {barcodeError}
              </Alert>
            )}

            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: 2,
                bgcolor: '#fafafa',
              }}
            >
              {barcodeEditMode && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Add barcode"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addBarcodeToDraft()
                      }
                    }}
                    placeholder="Type barcode and press Enter"
                  />
                  <IconButton
                    color="primary"
                    onClick={addBarcodeToDraft}
                    sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(barcodeEditMode ? barcodeDraft : barcodes).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No barcodes assigned.
                  </Typography>
                ) : (
                  (barcodeEditMode ? barcodeDraft : barcodes).map((barcode) => (
                    <Chip
                      key={barcode}
                      label={barcode}
                      size="small"
                      onDelete={
                        barcodeEditMode
                          ? () => removeBarcodeFromDraft(barcode)
                          : undefined
                      }
                      sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                    />
                  ))
                )}
              </Box>
            </Box>
          </Card>

          {/* Inventory Information */}
          <Card sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Inventory Information
              </Typography>
              <Button
                startIcon={<StorageIcon />}
                onClick={handleEditInventory}
                variant="outlined"
                size="small"
              >
                Update Inventory
              </Button>
            </Box>

            {product?.sku ? (
              <ProductInventoryLotsPanel sku={product.sku} refreshKey={inventoryRefreshKey} />
            ) : (
              <Alert severity="info">No Article Code available for this product</Alert>
            )}
          </Card>

          {/* Inventory Movements */}
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Inventory Movement History
            </Typography>

            {movements.length > 0 ? (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Before</TableCell>
                        <TableCell align="right">After</TableCell>
                        <TableCell align="right">Change</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Performed By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {movements.map((movement) => (
                        <TableRow key={movement.id}>
                          <TableCell>{formatDate(movement.created_at)}</TableCell>
                          <TableCell>
                            <Chip
                              label={movement.movement_type}
                              size="small"
                              sx={{
                                backgroundColor: getMovementTypeColor(movement.movement_type).bg,
                                color: getMovementTypeColor(movement.movement_type).color,
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{movement.quantity_before}</TableCell>
                          <TableCell align="right">{movement.quantity_after}</TableCell>
                          <TableCell align="right" sx={{
                            color: movement.quantity_after > movement.quantity_before ? '#2e7d32' : '#c62828',
                            fontWeight: 600
                          }}>
                            {movement.quantity_after > movement.quantity_before ? '+' : ''}
                            {movement.quantity_after - movement.quantity_before}
                          </TableCell>
                          <TableCell>{movement.reason}</TableCell>
                          <TableCell>{movement.performed_by_email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={movementsTotal}
                  rowsPerPage={movementsRowsPerPage}
                  page={movementsPage}
                  onPageChange={handleMovementsPageChange}
                  onRowsPerPageChange={handleMovementsRowsPerPageChange}
                />
              </>
            ) : (
              <Alert severity="info">No movement history found for this product</Alert>
            )}
          </Card>
        </Grid>

        {/* Product Images */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Product Images
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Logo Image */}
              {product.image_url?.logo && (
                <Paper sx={{ p: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Logo</Typography>
                  <CachedImage
                    src={product.image_url.logo}
                    alt={`${product.product_name} - Logo`}
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      height: 'auto',
                      maxHeight: '200px',
                      objectFit: 'contain'
                    }}
                  />
                </Paper>
              )}

              {/* Portrait Image */}
              {product.image_url?.portrait && (
                <Paper sx={{ p: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Portrait</Typography>
                  <CachedImage
                    src={product.image_url.portrait}
                    alt={`${product.product_name} - Portrait`}
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      height: 'auto',
                      maxHeight: '200px',
                      objectFit: 'contain'
                    }}
                  />
                </Paper>
              )}

              {/* Banner Image */}
              {product.image_url?.banner && (
                <Paper sx={{ p: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Banner</Typography>
                  <CachedImage
                    src={product.image_url.banner}
                    alt={`${product.product_name} - Banner`}
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      height: 'auto',
                      maxHeight: '200px',
                      objectFit: 'contain'
                    }}
                  />
                </Paper>
              )}

              {/* Additional Images */}
              {product.image_url?.additional_images?.map((imageUrl, index) => (
                <Paper key={`additional-${index}`} sx={{ p: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Additional {index + 1}</Typography>
                  <CachedImage
                    src={imageUrl}
                    alt={`${product.product_name} - Additional ${index + 1}`}
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      height: 'auto',
                      maxHeight: '200px',
                      objectFit: 'contain'
                    }}
                  />
                </Paper>
              ))}

              {!product.image_url?.logo && !product.image_url?.portrait && !product.image_url?.banner && (!product.image_url?.additional_images || product.image_url.additional_images.length === 0) && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 200,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1
                }}>
                  <Typography variant="body2" color="text.secondary">
                    No images available
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mt: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Edit Product Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>Edit Product</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && (
            <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Product Name"
              value={editFormData.product_name || ''}
              onChange={(e) => handleEditFormChange('product_name', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
            />

            <TextField
              label="Description"
              value={editFormData.description || ''}
              onChange={(e) => handleEditFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
              disabled={actionLoading}
            />

            <TextField
              label="Article Code"
              value={editFormData.sku || ''}
              onChange={(e) => handleEditFormChange('sku', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
            />

            <TextField
              label="TP/MAP"
              value={editFormData.tp_map || ''}
              onChange={(e) => handleEditFormChange('tp_map', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              placeholder="Optional"
              helperText="Optional trade price / MAP"
            />

            <TextField
              label="Category"
              value={editFormData.category || ''}
              onChange={(e) => handleEditFormChange('category', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
            />

            <FormControl fullWidth size="small" disabled={actionLoading}>
              <InputLabel>Division</InputLabel>
              <Select
                label="Division"
                value={editFormData.division_id || ''}
                onChange={(e) => handleEditFormChange('division_id', e.target.value)}
              >
                {divisions.map((division) => (
                  <MenuItem key={division.id} value={division.id}>
                    {division.name} ({division.slug})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Logo Image URL"
              value={editFormData.image_logo_url || ''}
              onChange={(e) => handleEditFormChange('image_logo_url', e.target.value)}
              fullWidth
              size="small"
              placeholder="https://example.com/logo.jpg"
              disabled={actionLoading}
              helperText="Main logo/thumbnail image"
            />

            <TextField
              label="Portrait Image URL"
              value={editFormData.image_portrait_url || ''}
              onChange={(e) => handleEditFormChange('image_portrait_url', e.target.value)}
              fullWidth
              size="small"
              placeholder="https://example.com/portrait.jpg"
              disabled={actionLoading}
              helperText="Portrait orientation image"
            />

            <TextField
              label="Banner Image URL"
              value={editFormData.image_banner_url || ''}
              onChange={(e) => handleEditFormChange('image_banner_url', e.target.value)}
              fullWidth
              size="small"
              placeholder="https://example.com/banner.jpg"
              disabled={actionLoading}
              helperText="Wide banner image"
            />

            <TextField
              label="Additional Images"
              value={editFormData.additional_images || ''}
              onChange={(e) => handleEditFormChange('additional_images', e.target.value)}
              fullWidth
              size="small"
              placeholder='["https://example.com/img1.jpg", "https://example.com/img2.jpg"]'
              disabled={actionLoading}
              helperText="JSON array of image URLs or comma-separated list"
              multiline
              rows={2}
            />

            <TextField
              label="Brand"
              value={editFormData.brand || ''}
              onChange={(e) => handleEditFormChange('brand', e.target.value)}
              fullWidth
              size="small"
              placeholder="Product brand"
              disabled={actionLoading}
            />

            <TextField
              label="VAT %"
              type="number"
              value={editFormData.vat ?? ''}
              onChange={(e) => handleEditFormChange('vat', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ step: '0.01', min: 0, max: 100 }}
              helperText="0–100, up to 2 decimals. Leave blank to keep the existing value."
            />

            <FormControl fullWidth size="small" disabled={actionLoading}>
              <InputLabel>Tax Category</InputLabel>
              <Select
                value={editFormData.tax_category || ''}
                onChange={(e) => handleEditFormChange('tax_category', e.target.value)}
                label="Tax Category"
              >
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="reduced">Reduced</MenuItem>
                <MenuItem value="zero">Zero Rate</MenuItem>
                <MenuItem value="exempt">Exempt</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={actionLoading}>
              <InputLabel>Unit of Measure</InputLabel>
              <Select
                value={editFormData.unit_of_measure || ''}
                onChange={(e) => handleEditFormChange('unit_of_measure', e.target.value)}
                label="Unit of Measure"
              >
                <MenuItem value="pcs">Pieces</MenuItem>
                <MenuItem value="kg">Kilograms</MenuItem>
                <MenuItem value="g">Grams</MenuItem>
                <MenuItem value="l">Liters</MenuItem>
                <MenuItem value="ml">Milliliters</MenuItem>
                <MenuItem value="m">Meters</MenuItem>
                <MenuItem value="cm">Centimeters</MenuItem>
                <MenuItem value="pack">Pack</MenuItem>
                <MenuItem value="box">Box</MenuItem>
                <MenuItem value="dozen">Dozen</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Pack Size"
              type="number"
              value={editFormData.pack_size ?? ''}
              onChange={(e) => handleEditFormChange('pack_size', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ min: 1, step: 1 }}
              error={Boolean(editFieldErrors.pack_size)}
              helperText={
                editFieldErrors.pack_size ||
                'Optional — units per pack (whole number greater than 0)'
              }
            />

            <FormControl fullWidth size="small" disabled={actionLoading}>
              <InputLabel>Pack Type</InputLabel>
              <Select
                value={editFormData.pack_type || ''}
                onChange={(e) => handleEditFormChange('pack_type', e.target.value)}
                label="Pack Type"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="carton">Carton</MenuItem>
                <MenuItem value="poly">Poly</MenuItem>
                <MenuItem value="sack">Sack</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_active !== false}
                  onChange={(e) => handleEditFormChange('is_active', e.target.checked)}
                  disabled={actionLoading}
                />
              }
              label="Product is Active"
            />
            {editFormData.source !== 'external' && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.is_purchasable !== false}
                      onChange={(e) => handleEditFormChange('is_purchasable', e.target.checked)}
                      disabled={actionLoading}
                    />
                  }
                  label="Available for purchase"
                />
                <Typography variant="caption" sx={{ color: '#666', mt: -1 }}>
                  When off, the product is hidden from purchase catalog and cannot be added to purchase
                  orders. POS / sales are unaffected.
                </Typography>
              </>
            )}
            {editFormData.source === 'external' && (
              <Typography variant="caption" sx={{ color: '#666' }}>
                External products are not available for purchase orders. Purchasable cannot be enabled.
              </Typography>
            )}

            {editFormData.source !== 'external' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.dsd === true}
                    onChange={(e) => handleEditFormChange('dsd', e.target.checked)}
                    disabled={actionLoading}
                  />
                }
                label="DSD"
              />
            )}
            {editFormData.source === 'external' && (
              <Typography variant="caption" sx={{ color: '#666' }}>
                DSD cannot be enabled for external products.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button onClick={handleEditDialogClose} disabled={actionLoading} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={actionLoading}
            sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inventory Management Dialog */}
      <Dialog open={inventoryDialogOpen} onClose={handleInventoryDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
          Update Inventory - {product?.product_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && (
            <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Quantity on Hand"
              type="number"
              value={inventoryFormData.quantity_on_hand}
              onChange={(e) => handleInventoryFormChange('quantity_on_hand', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ step: '1', min: '0' }}
            />

            <TextField
              label="Minimum Stock Level"
              type="number"
              value={inventoryFormData.minimum_stock_level}
              onChange={(e) => handleInventoryFormChange('minimum_stock_level', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ step: '1', min: '0' }}
            />

            <TextField
              label="Maximum Stock Level"
              type="number"
              value={inventoryFormData.maximum_stock_level}
              onChange={(e) => handleInventoryFormChange('maximum_stock_level', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ step: '1', min: '0' }}
            />

            <TextField
              label="Reorder Point"
              type="number"
              value={inventoryFormData.reorder_point}
              onChange={(e) => handleInventoryFormChange('reorder_point', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ step: '1', min: '0' }}
            />

            <TextField
              label="Reserved Quantity"
              type="number"
              value={inventoryFormData.reserved_quantity}
              onChange={(e) => handleInventoryFormChange('reserved_quantity', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              inputProps={{ step: '0.001', min: '0' }}
            />

            <TextField
              label="Location"
              value={inventoryFormData.location}
              onChange={(e) => handleInventoryFormChange('location', e.target.value)}
              fullWidth
              size="small"
              disabled={actionLoading}
              placeholder="e.g., Warehouse A, Shelf 1"
            />

            <TextField
              label="Notes"
              value={inventoryFormData.notes}
              onChange={(e) => handleInventoryFormChange('notes', e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
              disabled={actionLoading}
              placeholder="Additional notes about this inventory record"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
          <Button onClick={handleInventoryDialogClose} color="inherit" disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveInventory}
            variant="contained"
            startIcon={actionLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={actionLoading}
            sx={{
              bgcolor: '#e90000',
              '&:hover': { bgcolor: '#d32f2f' }
            }}
          >
            {actionLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProductDetailPage